import { string } from "prop-types"
import internal from "stream";

export const COMPANY_NAME = "Paras Door";
export const SUPPORT_MOBILE_NUMBER = "9736464123";

export interface Party {
    id: number;
    name: string;
    mobileNumber: string;
    contactPerson: string;
    gstin: string;
    billingAddress1: string;
    billingAddress2: string;
    billingCity: string;
    billingPinCode: string;
    billingStateCode: number;
    dealerTypeName: string;
    totalBalance: number;
    chequeNumber: string;
    partyId: number;
    accoutId: number;
    status: string;
    isExpanded: boolean;
    notes: string;
    accountId: number;
    enableAppLogin: boolean;
    password: string;
}

export interface Transaction {
    id: number;
    closingBalance: number;
    credit: number;
    debit: number;
    description: string;
    openingBalance: number;
    transactionRefNumber: number;
    transactionType: string;
    voucherId: number;
    voucherNumber: string;
    voucherType: string;
    transactionDate: string;
}

export interface BusinessSummaryModel {
    openingBalance: number;
    sales: number;
    creditNote: number;
    return: number;
    receipt: number;
    journal: number;
    business: number;
    closingBalance: number;
    newBalance: number;
    oldBalance: number;
    partyName: string;
    mobile: string;
    address: string;
    contact: string;
}

export interface PendingInvoices {
    voucherDate: string;
    paymentDate: string;
    voucherNumber: string;
    billAmount: number;
    paidAmount: number;
    pendingAmount: number;
    dueDays: number;
    isSelected?: boolean;
}

export interface SalesPerson {
    id: number;
    name: string;
    mobileNumber: string;
    isActive: boolean;
}

export interface Sale {
    id: number;
    voucherNumber: string;
    date: string;
    party: Party;
    billAmount: number;
    vehicleNumber: string;
    driverName: string;
    driverNumber: string;
    isExpanded: boolean;
}

export interface SaleOrder {
    id: number;
    voucherNumber: string;
    date: string;
    party: Party;
    billAmount: number;
    vehicleNumber: string;
    driverName: string;
    driverNumber: string;
    isExpanded: boolean;
    status: number;
    statsDisplay: string;
}

export interface CreditNote {
    id: number;
    voucherNumber: string;
    date: string;
    party: Party;
    amount: number;
    notes: string;
    isExpanded: boolean;
}

export interface Receipt {
    id: number;
    voucherNumber: string;
    date: string;
    party: Party;
    amount: number;
    notes: string;
    paymentType: string;
    referenceNumber: string;
    isExpanded: boolean;
}

export interface VoucherSummary {
    numberOfInvoices: number;
    billAmount: number;
}

export interface DispatchDetails {
    dispatchedThrough: string,
    vehicleNumber: string,
    driverName: string,
    driverNumber: string
}

export interface ProductGroup {
    id: number;
    name: string;
    code: string;
    imageUrl: string;
}

export interface Product {
    id: number;
    masterId: number;
    serverId: number;
    isActive: boolean;
    name: string;
    searchName: string;
    code: string;
    type: string;
    hsnCode: string;
    groupId: number;
    groupName: string;
    subGroupId: number;
    subGroupName: string;
    brandId: number;
    brandName: string;
    unitOfMeasureId: number;
    unitOfMeasureName: string;
    unitOfMeasureCode: string;
    length: number;
    width: number;
    thickness: number;
    unit: number;
    squareFeet: number;
    trackIndividualItem: boolean;
    trackInventory: boolean;
    needsNarration: boolean;
    defaultNarration: string;
    imagePath: string;
    price: number;
    priceIncludesTax: boolean;
    discount: number;
    isDiscountInPercentage: boolean;
    costPrice: number;
    costPriceIncludesTax: boolean;
    averageCostPrice: number;
    mrp: number;
    cgstTax: Tax;
    sgstTax: Tax;
    igstTax: Tax;
    cess: Tax;
    vat: Tax;
    saleAccountId: number;
    purchaseAccountId: number;
    createdBy: number;
    createdAt: string;
    modifiedBy: number;
    modifiedAt: string;
    displayName: string | null;
    stockQuantity: number;
    options: Option[];
    pricingOptions: any | null;
    nameLower: string;
    discountDisplay: string;
}


export class SaleOrder {
    id: number;
    voucherNumber: string;
    date: string;
    dueDate: string;
    departmentId: number;
    series: string;
    party: Party;
    branch: any;
    shippingAddress1: string;
    shippingAddress2: string;
    shippingCity: string;
    shippingPinCode: string;
    shippingStateCode: number;
    supplyStateId: number;
    notes: string;
    items: OrderItem[];
    taxableValue: number;
    discount: number;
    cgstTax: number;
    sgstTax: number;
    igstTax: number;
    netValue: number;
    roundOff: number;
    billAmount: number;
    status: number;
    isApproved: boolean;
    isExpanded: boolean;
    productionPlantName: string;
    productionPlantId: number;
    extraInformation: string;
    documents: OrderDocument[];

    constructor() {
        this.items = [];
        this.documents = [];
        this.status = 5;
        this.departmentId = 1;
        this.supplyStateId = 29;
        this.shippingStateCode = 29;
        this.isApproved = false;
    }
}

export class OrderItem {
    productMaster: ProductMaster;
    product: Product;
    style: ProductStyle;
    options: any[];
    price: number;
    quantity: number;
    taxableValue: number;
    priceIncludesTax: boolean;
    discountPercentage: number;
    isDiscountInPercentage: boolean;
    discount: number;
    cgstTaxPercentage: number;
    sgstTaxPercentage: number;
    igstTaxPercentage: number;
    cgstTax: number;
    sgstTax: number;
    igstTax: number;
    squareFeet: number;
    unitId: number;
    unit: string;
    netValue: number;
    notes: string;
    customImageUrl: string;
    packedQuantity: number;
    pressedQuantity: number;
    pendingQuantity: number;
}

export class OrderDocument {
    id: number;
    voucherId: number;
    name: string;
    notes: string;
    fileUrl: string;
    uploadedAt: string;
}

export interface Company {
    id: number;
    name: string;
    legalName: string;
    shortName: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    pincode: string;
    gSTIN: string;
    stateId: number;
    stateName: string;
    phoneNumber: string;
    email: string;
    website: string;
    description: string;
    type: string;
    logo: string;
    isMultiGodown: boolean;
    isMultiDepartment: boolean;
    defaultDepartmentId: number;
    defaultGodownId: number;
    createdAt: string;
    createdBy: number;
    modifiedAt: string;
    modifiedBy: number;
    numberOfCounters: number;
    address: string;
    bankAccount: BankAccount;
}

export interface BankAccount {
    id: number;
    name: string;
    ifscCode: string;
    micr: string;
    upiId: string;
    branch: string;
    bankName: string;
    address: string;
    bankAccountType: string;
    accountNumber: string;
    balance: number;
    createdAt: string;
    createdBy: number;
    modifiedAt: string;
    modifiedBy: number;
}

export interface ProductMaster {
    id: number;
    name: string;
    group: Group;
    brand: Brand;
    brandId: number;
    groupId: number;
    code: string;
    unitOfMeasureId: number;
    type: string;
    products: Product[];
    sizes: any | null;
    varients: any | null;
    hsnCode: string;
    taxId: number;
    files: any[];
    images: ProductImage[];
    imageUrl: string;
    createdBy: number;
    createdAt: string;
    modifiedBy: number;
    modifiedAt: string;
    hasVarients: boolean;
    enableOnlineSales: boolean;
    enablePOSSales: boolean;
    enableBackOrder: boolean;
    sizeChart: any | null;
    description: string | null;
    isActive: boolean;
    trackIndividualItem: boolean;
    trackInventory: boolean;
    needsNarration: boolean;
    defaultNarration: string | null;
    price: number;
    priceIncludesTax: boolean;
    discount: number;
    isDiscountInPercentage: boolean;
    costPrice: number;
    costPriceIncludesTax: boolean;
    averageCostPrice: number;
    supportsBatch: boolean;
    hasExpiry: boolean;
    hasWarranty: boolean;
    minimumQuantity: number;
    unitOfMeasureName: string;
    unitOfMeasureCode: string;
    cgstTax: Tax;
    sgstTax: Tax;
    igstTax: Tax;
    cess: Tax;
    options: Option[];
    pricingOptions: any[];
}

export interface Group {
    id: number;
    serverId: number;
    name: string;
    code: string;
    size: any | null;
    taxTypeId: number;
    taxTypeName: string | null;
    createdAt: string;
    createdBy: number;
    modifiedAt: string;
    modifiedBy: number;
    sizeList: any | null;
}

export interface Brand {
    id: number;
    serverId: number;
    name: string;
    description: string | null;
    code: string;
    createdAt: string;
    createdBy: number;
    modifiedAt: string;
    modifiedBy: number;
}

export interface Tax {
    type: number;
    id: number;
    name: string | null;
    percentage: number;
    inputTaxAccountId: number;
    outputTaxAccountId: number;
    createdAt: string;
    createdBy: number;
    modifiedAt: string;
    modifiedBy: number;
}

export interface Option {
    name: string;
    type: string;
    value: string;
    values: string[] | null;
}

export interface ProductImage {
    id: number;
    productBrandId: number;
    productMasterId: number;
    url: string;
    name: string | null;
    uploadedAt: string;
    uploadedBy: number;
}

export interface ProductStyle {
    id: number;
    productId: number;
    name: string;
    imageUrl: string;
    notes: string;
    groupName: string;
}

export interface BankCheque {
    id: number;
    chequeNumber: string;
    date: string;
    clearedOn: string;
    chequeDate: string;
    partyName: string;
    bankName: string;
    bankBranch: string;
    amount: number;
    status: string;
    notes: string;
    isExpanded: boolean;
}

export const OrderStatuses: { id: number, name: string, class: string, canShow: boolean }[] = [
    { id: 1, name: 'Received', class: "badge bg-danger text-white", canShow: false },
    { id: 2, name: 'Confirmed', class: "badge bg-primary text-white", canShow: true },
    { id: 3, name: 'Running', class: "badge bg-info text-white", canShow: true },
    { id: 4, name: 'Completed', class: "badge bg-success text-white", canShow: true },
    { id: 5, name: 'Dispatched', class: "badge bg-success text-white", canShow: false },
    { id: 6, name: 'Cancelled', class: "badge bg-secondary text-white", canShow: false }
];